import React, { useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable/Clickable'
import moment from 'moment'
import Expander from 'components/utils/Expander'
import PageBanner from 'LuxKit/Banners/PageBanner'
import useBusinessTravellerItemPolicyBudgetFetcher from 'businessTraveller/hooks/useBusinessTravellerItemPolicyBudgetFetcher'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import BusinessTravellerItemPolicyHotelBudgetTooltipModal from './BusinessTravellerItemPolicyHotelBudgetTooltipModal'
import BusinessTravellerPolicyDebugButton from 'businessTraveller/components/policy-debug/BusinessTravellerPolicyDebugButton'
import Group from 'components/utils/Group'

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
`

interface Props {
  checkInDate?: string
  checkOutDate?: string
  roomCount?: number
  className?: string
  offer?: App.AnyOffer
}

function BusinessTravellerItemPolicyStickyBudgetBar(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    roomCount = 1,
    className,
    offer,
  } = props

  const numberOfNights = useMemo(() => {
    if (!checkInDate || !checkOutDate) {
      return undefined
    }
    return moment(checkOutDate).diff(checkInDate, 'days') || 1
  }, [checkInDate, checkOutDate])

  const {
    budgetView,
    cacheKey,
    itemPolicyData,
    legacyBudgetData,
    legacyBudgetKey,
    employeeIds,
  } = useBusinessTravellerItemPolicyBudgetFetcher(offer, {
    numberOfNights,
    roomCount,
    enableShadowCompare: true,
  })

  const showModal = useContext(ModalContext)
  const isBudgetBarShown = budgetView?.isShown ?? false

  const toggleModal = useCallback(() => {
    if (budgetView?.travellerLines && numberOfNights != null) {
      showModal(
        <BusinessTravellerItemPolicyHotelBudgetTooltipModal
          travellerLines={budgetView.travellerLines}
          numberOfNights={numberOfNights}
        />,
      )
    }
  }, [budgetView?.travellerLines, numberOfNights, showModal])

  const budgetBarRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(budgetBarRef, isBudgetBarShown ? 'sticky' : 'hidden')
  useHeaderStateObserver('businessTravellerBudgetBar', budgetBarRef, isBudgetBarShown)

  if (!isBudgetBarShown || !budgetView) {
    return null
  }

  const showMultiTravellerTooltip = budgetView.isMultiTraveller
  const budgetLabel = budgetView.isMultiTraveller ? 'Minimum accommodation budget' : 'Accommodation budget'

  let perRoomAmountDisplay: React.ReactNode
  if (budgetView.showPerRoomRange) {
    perRoomAmountDisplay = (
      <>
        <FormatCurrency
          currency={budgetView.currencyCode ?? undefined}
          value={budgetView.perRoomAmount}
          format="dynamicDollar"
        />
        {' – '}
        <FormatCurrency
          currency={budgetView.currencyCode ?? undefined}
          value={budgetView.maxPerRoomAmount}
          format="dynamicDollar"
        />
      </>
    )
  } else {
    perRoomAmountDisplay = (
      <FormatCurrency
        currency={budgetView.currencyCode ?? undefined}
        value={budgetView.perRoomAmount}
        format="dynamicDollar"
      />
    )
  }

  return (
    <StickyContainer className={className} ref={budgetBarRef}>
      <Expander expanded={!!budgetView}>
        <PageBanner
          kind="promotional-primary"
          align="center"
          description={
            <b>
              {budgetLabel}: {perRoomAmountDisplay} per room
              {roomCount > 1 && (
                <>
                  {' '}
                  (
                  <FormatCurrency
                    currency={budgetView.currencyCode ?? undefined}
                    value={budgetView.totalAmount}
                    format="dynamicDollar"
                  />{' '}
                  total)
                </>
              )}
            </b>
          }
          trailingContent={
            <Group direction="horizontal" gap={8} verticalAlign="center">
              {showMultiTravellerTooltip && (
                <Clickable onClick={toggleModal}>
                  <LineInfoCircleIcon size="S" />
                </Clickable>
              )}
              <BusinessTravellerPolicyDebugButton
                source="item_policy"
                vertical="hotel"
                surface="budget"
                primaryData={itemPolicyData}
                comparisonData={legacyBudgetData}
                cacheKey={legacyBudgetKey ?? cacheKey}
                employeeIds={employeeIds}
              />
            </Group>
          }
        />
      </Expander>
    </StickyContainer>
  )
}

export default BusinessTravellerItemPolicyStickyBudgetBar
